/**
 * "신고 처리" — moderation queue admin screen.
 *
 * Backend (already existed, admin-protected via requireAdminKey):
 * - GET  /api/reports?status=&priority=&limit=&offset= -> { reports, total, limit, offset }
 * - PATCH /api/reports/:id { status?, priority?, assignedTo?, resolution?, actionTaken?, adminNotes? } -> { report }
 * See functions/api/reports/index.ts and functions/api/reports/[id].ts (shared/schema-d1.ts `reports` table
 * for the enum values below).
 */
import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, Flag, XCircle } from "lucide-react";
import { apiGet, apiPatch } from "./api";
import { queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import AdminPageLayout from "./AdminPageLayout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";

type ReportStatus = "pending" | "reviewing" | "resolved" | "dismissed";
type ReportPriority = "low" | "normal" | "high" | "urgent";
type ReportActionTaken = "warning" | "content_removed" | "user_suspended" | "user_banned" | "no_action";

type Report = {
  id: number;
  reporterId: number;
  reporterEmail: string | null;
  reportedType: string;
  reportedId: number;
  reportedUserId: number | null;
  reason: string;
  category: string;
  description: string;
  evidenceUrls: string | null;
  status: ReportStatus;
  priority: ReportPriority | null;
  assignedTo: number | null;
  resolution: string | null;
  actionTaken: ReportActionTaken | null;
  adminNotes: string | null;
  reportedAt: string;
  reviewedAt: string | null;
  resolvedAt: string | null;
  updatedAt: string | null;
  reporterName: string | null;
  reporterUserEmail: string | null;
};

type ReportsResponse = { reports: Report[]; total: number; limit: number; offset: number };

const statusOptions: { value: ReportStatus | "all"; label: string }[] = [
  { value: "all", label: "전체" },
  { value: "pending", label: "대기중" },
  { value: "reviewing", label: "검토중" },
  { value: "resolved", label: "처리완료" },
  { value: "dismissed", label: "기각" },
];
const priorityOptions: { value: ReportPriority | "all"; label: string }[] = [
  { value: "all", label: "전체" },
  { value: "low", label: "낮음" },
  { value: "normal", label: "보통" },
  { value: "high", label: "높음" },
  { value: "urgent", label: "긴급" },
];
const actionTakenOptions: { value: ReportActionTaken; label: string }[] = [
  { value: "no_action", label: "조치 없음" },
  { value: "warning", label: "경고" },
  { value: "content_removed", label: "콘텐츠 삭제" },
  { value: "user_suspended", label: "이용 정지" },
  { value: "user_banned", label: "영구 정지" },
];
const statusLabel: Record<string, string> = Object.fromEntries(statusOptions.map((o) => [o.value, o.label]));
const priorityLabel: Record<string, string> = Object.fromEntries(priorityOptions.map((o) => [o.value, o.label]));
const reportedTypeLabel: Record<string, string> = {
  article: "기사",
  comment: "댓글",
  market_listing: "중고거래",
  community_talk: "동네 톡톡",
  city_classified: "생활광고",
  user: "사용자",
};
const priorityBadgeVariant: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  low: "outline",
  normal: "secondary",
  high: "default",
  urgent: "destructive",
};

export default function ReportsManagement() {
  const { toast } = useToast();
  const [status, setStatus] = useState<string>("all");
  const [priority, setPriority] = useState<string>("all");
  const [resolveTarget, setResolveTarget] = useState<Report | null>(null);
  const [resolveStatus, setResolveStatus] = useState<ReportStatus>("resolved");
  const [resolveActionTaken, setResolveActionTaken] = useState<ReportActionTaken>("no_action");
  const [resolution, setResolution] = useState("");
  const [adminNotes, setAdminNotes] = useState("");

  const params = new URLSearchParams();
  if (status !== "all") params.set("status", status);
  if (priority !== "all") params.set("priority", priority);
  const queryString = params.toString();

  const list = useQuery({
    queryKey: ["admin-reports", status, priority],
    queryFn: () => apiGet<ReportsResponse>(`/api/reports${queryString ? `?${queryString}` : ""}`),
  });

  const patch = useMutation({
    mutationFn: ({ id, body }: { id: number; body: Record<string, unknown> }) =>
      apiPatch(`/api/reports/${id}`, body),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["admin-reports"] });
      if (variables.body.resolution !== undefined) {
        toast({ title: "신고를 처리했습니다" });
        closeResolveDialog();
      } else {
        toast({ title: "신고를 갱신했습니다" });
      }
    },
    onError: (error: Error) => toast({ title: "갱신 실패", description: error.message, variant: "destructive" }),
  });

  const openResolveDialog = (report: Report) => {
    setResolveTarget(report);
    setResolveStatus(report.status === "pending" ? "resolved" : report.status);
    setResolveActionTaken(report.actionTaken || "no_action");
    setResolution(report.resolution || "");
    setAdminNotes(report.adminNotes || "");
  };
  const closeResolveDialog = () => {
    setResolveTarget(null);
    setResolution("");
    setAdminNotes("");
  };

  const items = list.data?.reports || [];

  let evidenceUrls: string[] = [];
  if (resolveTarget?.evidenceUrls) {
    try {
      const parsed = JSON.parse(resolveTarget.evidenceUrls);
        if (Array.isArray(parsed)) {
          evidenceUrls = parsed.filter((value): value is string => {
            if (typeof value !== "string") return false;
            try {
              const protocol = new URL(value).protocol;
              return protocol === "https:" || protocol === "http:";
            } catch {
              return false;
            }
          });
        }
    } catch {
      // ignore malformed evidence JSON
    }
  }

  return (
    <AdminPageLayout
      title="신고 처리"
      description="기사·댓글·중고거래·동네 톡톡·생활광고·사용자에 대한 신고를 확인하고 처리합니다."
      icon={<Flag className="w-8 h-8" />}
      loading={list.isLoading}
      error={list.error ? (list.error as Error).message : null}
    >
      <Card>
        <CardContent className="space-y-4 pt-6">
          <div className="flex flex-wrap gap-3">
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
              <SelectContent>
                {statusOptions.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={priority} onValueChange={setPriority}>
              <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
              <SelectContent>
                {priorityOptions.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="ml-auto text-sm text-muted-foreground self-center">
              {list.data ? `총 ${list.data.total}건` : ""}
            </div>
          </div>

          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>신고 대상</TableHead>
                  <TableHead>사유</TableHead>
                  <TableHead>신고자</TableHead>
                  <TableHead>우선순위</TableHead>
                  <TableHead>상태</TableHead>
                  <TableHead className="text-right">작업</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((report) => (
                  <TableRow key={report.id}>
                    <TableCell>
                      <div className="font-medium">
                        {reportedTypeLabel[report.reportedType] || report.reportedType} #{report.reportedId}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {new Date(report.reportedAt).toLocaleString("ko-KR")}
                      </div>
                    </TableCell>
                    <TableCell className="max-w-xs">
                      <div className="text-sm">{report.reason} · {report.category}</div>
                      <div className="truncate text-xs text-muted-foreground" title={report.description}>
                        {report.description}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">
                      {report.reporterName || report.reporterUserEmail || report.reporterEmail || `#${report.reporterId}`}
                    </TableCell>
                    <TableCell>
                      <Select
                        value={report.priority || "normal"}
                        onValueChange={(value) => patch.mutate({ id: report.id, body: { priority: value } })}
                      >
                        <SelectTrigger className="w-32">
                          <Badge variant={priorityBadgeVariant[report.priority || "normal"]} className="text-xs">
                            {priorityLabel[report.priority || "normal"]}
                          </Badge>
                        </SelectTrigger>
                        <SelectContent>
                          {priorityOptions.filter((o) => o.value !== "all").map((o) => (
                            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Badge variant={report.status === "resolved" ? "secondary" : report.status === "dismissed" ? "outline" : "default"}>
                        {statusLabel[report.status]}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" onClick={() => openResolveDialog(report)}>
                        <AlertTriangle className="mr-1 h-4 w-4" />처리
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {!list.isLoading && items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="py-10 text-center text-muted-foreground">
                      해당 조건의 신고가 없습니다.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={!!resolveTarget} onOpenChange={(open) => { if (!open) closeResolveDialog(); }}>
        <DialogContent className="sm:max-w-[560px]">
          <DialogHeader>
            <DialogTitle>신고 처리</DialogTitle>
            <DialogDescription>
              {resolveTarget && (
                <>
                  {reportedTypeLabel[resolveTarget.reportedType] || resolveTarget.reportedType} #{resolveTarget.reportedId} ·{" "}
                  {resolveTarget.reason} / {resolveTarget.category}
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          {resolveTarget && (
            <div className="space-y-3">
              <div className="rounded-md border bg-muted/40 p-3 text-sm">
                <p className="whitespace-pre-wrap">{resolveTarget.description}</p>
                {evidenceUrls.length > 0 && (
                  <div className="mt-2 space-y-1">
                    <Label className="text-xs text-muted-foreground">증거 자료</Label>
                    {evidenceUrls.map((url, i) => (
                      <a
                        key={i}
                        href={url}
                        target="_blank"
                        rel="noreferrer"
                        className="block truncate text-xs text-primary underline"
                      >
                        {url}
                      </a>
                    ))}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>상태</Label>
                  <Select value={resolveStatus} onValueChange={(value) => setResolveStatus(value as ReportStatus)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {statusOptions.filter((o) => o.value !== "all").map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>조치 내용</Label>
                  <Select value={resolveActionTaken} onValueChange={(value) => setResolveActionTaken(value as ReportActionTaken)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {actionTakenOptions.map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-1">
                <Label htmlFor="resolution">처리 내용 (신고자 확인용)</Label>
                <Textarea
                  id="resolution"
                  rows={3}
                  value={resolution}
                  onChange={(e) => setResolution(e.target.value)}
                  placeholder="예: 확인 결과 규정 위반으로 콘텐츠를 삭제했습니다."
                />
              </div>

              <div className="space-y-1">
                <Label htmlFor="admin-notes">관리자 메모 (내부용)</Label>
                <Textarea
                  id="admin-notes"
                  rows={2}
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="내부 검토 기록"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={closeResolveDialog}>
              <XCircle className="mr-1 h-4 w-4" />취소
            </Button>
            <Button
              type="button"
              disabled={patch.isPending}
              onClick={() =>
                resolveTarget &&
                patch.mutate({
                  id: resolveTarget.id,
                  body: {
                    status: resolveStatus,
                    actionTaken: resolveActionTaken,
                    resolution: resolution.trim(),
                    adminNotes: adminNotes.trim() || undefined,
                  },
                })
              }
            >
              <CheckCircle2 className="mr-1 h-4 w-4" />처리 완료
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminPageLayout>
  );
}
